export const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null =>
  root.querySelector<T>(sel);

export const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  [...root.querySelectorAll<T>(sel)];

/** Assert-get an element by selector (throws if missing — used for known app shell nodes). */
export const must = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T => {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error(`missing element: ${sel}`);
  return el;
};

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string>,
  html?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (html != null) node.innerHTML = html;
  return node;
}

export const esc = (s: unknown): string =>
  String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));

export const p2 = (n: number): string => String(n).padStart(2, '0');
export const hms = (t: number): string => {
  const d = new Date(t);
  return `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
};
export const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
export const dist = (ax: number, ay: number, bx: number, by: number): number => Math.hypot(ax - bx, ay - by);

/** FLIP: run `mutate` (DOM reorder), then animate every moved child from its old box. */
export function flip(container: HTMLElement, childSel: string, mutate: () => void, ms = 220): void {
  const first = new Map<Element, DOMRect>($$(childSel, container).map((c) => [c, c.getBoundingClientRect()]));
  mutate();
  for (const c of $$(childSel, container)) {
    const a = first.get(c);
    if (!a) continue;
    const b = c.getBoundingClientRect();
    const dx = a.left - b.left;
    const dy = a.top - b.top;
    if (!dx && !dy) continue;
    const node = c as HTMLElement;
    node.style.transition = 'none';
    node.style.transform = `translate(${dx}px,${dy}px)`;
    requestAnimationFrame(() => {
      node.style.transition = `transform ${ms}ms cubic-bezier(.2,.6,.2,1)`;
      node.style.transform = '';
    });
    node.addEventListener('transitionend', () => {
      node.style.transition = '';
      node.style.transform = '';
    }, { once: true });
  }
}
